import { QueryKey } from '@tanstack/react-query';

export type TradeQueryContext = {
  uid: string;
  baseTokenAddress: string;
  marketAddress: string;
};

const getQueryType = (queryKey: QueryKey) => {
  if (typeof queryKey[0] === 'string') return queryKey[0];
  if (typeof queryKey[1] === 'string') return queryKey[1];
  return undefined;
};

// Collateral addresses may arrive checksummed or lowercase from different
// sources; compare case-insensitively so trade cache invalidation isn't missed.
const eqAddr = (a: unknown, b: unknown) =>
  typeof a === 'string' && typeof b === 'string' ? a.toLowerCase() === b.toLowerCase() : a === b;

// Real runtime query keys from useSSEQuery/useSSEInfiniteQuery are 3-tuples:
// [undefined, queryType, searchParams] (they pass the caller's queryKey
// through as-is — see FACT-2). Scan for the params object instead of
// assuming a fixed index so this keeps working if a future hook changes the
// tuple shape; SignedUserAssets/EventMarketTrades/SingerTokenBalance are
// handled before this is ever consulted, so their non-params-object keys
// (an api instance, a token list) can't accidentally match here either.
const getSearchParams = (queryKey: QueryKey): Record<string, unknown> | undefined =>
  queryKey.find(
    el =>
      !!el &&
      typeof el === 'object' &&
      !Array.isArray(el) &&
      ('marketAddress' in (el as object) || 'baseTokenAddress' in (el as object) || 'uid' in (el as object))
  ) as Record<string, unknown> | undefined;

export const shouldRefetchTradeQuery = (queryKey: QueryKey, ctx: TradeQueryContext) => {
  const queryType = getQueryType(queryKey);

  if (queryType === 'SignedUserAssets') {
    return queryKey[2] === ctx.uid && eqAddr(queryKey[3], ctx.baseTokenAddress);
  }

  // Grouped-event trades feed: ['EventMarketTrades', baseTokenAddress, marketAddress].
  // Matched on marketAddress alone: the key's baseTokenAddress is whatever
  // base-token lens is currently selected in the UI, which can differ from
  // the traded market's actual collateral (ctx.baseTokenAddress) — and
  // GetMarketTrades ignores the collateral param server-side anyway, so a
  // marketAddress match is both necessary and sufficient here.
  if (queryType === 'EventMarketTrades') {
    return eqAddr(queryKey[2], ctx.marketAddress);
  }

  // User's own on-chain balance (buy-side funding source). No market
  // dimension to scope by — any trade by this user may have moved it.
  if (queryType === 'SingerTokenBalance') {
    return true;
  }

  const searchParams = getSearchParams(queryKey);
  if (!searchParams) return false;

  if (queryType === 'UserPositions' || queryType === 'UserTrades') {
    // Portfolio-wide lists (no marketAddress in the query) are "all of this
    // user's positions/history" — always refresh those; market-scoped variants
    // only refresh when it's the traded market.
    return (
      searchParams.uid === ctx.uid &&
      eqAddr(searchParams.baseTokenAddress, ctx.baseTokenAddress) &&
      (searchParams.marketAddress == null || searchParams.marketAddress === ctx.marketAddress)
    );
  }

  // Same lens-vs-collateral caveat as EventMarketTrades above: match on
  // marketAddress alone, not baseTokenAddress (a market's collateral is fixed
  // and implied by its address; the query key's baseTokenAddress is just the
  // currently-selected UI lens, which routinely differs from it).
  if (queryType === 'MarketDetail' || queryType === 'MarketTrades') {
    return eqAddr(searchParams.marketAddress, ctx.marketAddress);
  }

  return false;
};
